// Dò ca "init ở thư mục con, làm việc ở thư mục cha" — nguồn của một lớp sự cố
// telemetry IM LẶNG hoàn toàn.
//
// Cơ chế hỏng (đo 2026-09-16, user nambu.akio@nal.vn): Claude Code chỉ nạp hook từ
// `.claude/settings.json` của ĐÚNG thư mục được mở, KHÔNG dò xuống thư mục con. Nên
// khi `avatar init` chạy ở `<cha>/avatar/` mà user mở Claude Code ở `<cha>/`, toàn bộ
// hook telemetry nằm ngoài tầm với ⇒ 0 hook chạy ⇒ 0 event sinh ra. Không có hàng đợi
// để truy, không có lỗi để đọc: `avatar` (binary global) và Claude Code đều chạy bình
// thường. Triệu chứng DUY NHẤT là dashboard telemetry thiếu hẳn user đó.
//
// Vì sao phải dò ở đây thay vì sửa resolver: `resolveAvatarWorkspaceRootFromCwd` đi
// NGƯỢC LÊN (walk-up) theo thiết kế — đứng trong subdir của workspace thì tìm ra root.
// Ca này ngược chiều hẳn: workspace nằm BÊN DƯỚI cwd, walk-up không bao giờ thấy. Nới
// resolver để nó nhìn xuống là phá tính xác định của MỌI lệnh (`sync`/`status`/`init`
// sẽ tự nhận vơ workspace con). Nên đây là phép đo RIÊNG, chỉ `doctor` dùng.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists } from "./filesystem-helpers.js";
import { WORKSPACE_MARKER_RELATIVE } from "./workspace-marker-constants.js";

/** Thư mục con là Avatar workspace, kèm ảnh chụp entry top-level của nó. */
export interface NestedWorkspaceCandidate {
  /** Tên thư mục con (không phải path) — dùng để in `./<tên>` cho người đọc. */
  name: string;
  /** Path tuyệt đối tới workspace con. */
  path: string;
  /**
   * Entry top-level của thư mục con, ĐÃ SẮP XẾP. Gồm CẢ entry ẩn (`.mattermost`,
   * `.git`...) vì chúng là bằng chứng "thư mục con có nội dung thật của user" —
   * chính là vế quyết định `--fix` được phép dời hay không.
   */
  entries: string[];
}

/**
 * Quét MỘT tầng con của `cwd` tìm Avatar workspace.
 *
 * Trả `[]` khi CHÍNH `cwd` đã là workspace — ca bình thường, không có gì để cảnh báo.
 * Đây là điều kiện đầu tiên và là lý do hàm không bao giờ báo động oan cho người dùng
 * đang đứng đúng chỗ.
 *
 * Bỏ qua: thư mục ẩn (`.git`, `.vscode`...) và `node_modules` — không ai `avatar init`
 * vào đó, mà `node_modules` thì quét tốn vô ích. KHÔNG đệ quy sâu hơn 1 tầng: ca thật
 * là "init nhầm vào thư mục con ngay cạnh", còn quét sâu vừa chậm vừa dễ nhặt phải
 * workspace thử nghiệm của người dùng (đo 2026-08-20: 42 marker trên máy dev).
 *
 * Fail-soft: `cwd` không đọc được ⇒ `[]`. Đây là check chẩn đoán, không được làm chết
 * cả lượt `doctor`.
 */
export async function detectNestedAvatarWorkspaces(
  cwd: string,
): Promise<NestedWorkspaceCandidate[]> {
  if (await pathExists(join(cwd, WORKSPACE_MARKER_RELATIVE))) return [];

  let children: import("node:fs").Dirent[];
  try {
    children = await fs.readdir(cwd, { withFileTypes: true });
  } catch {
    return [];
  }

  const found: NestedWorkspaceCandidate[] = [];
  for (const child of children) {
    // `isDirectory()` false cho symlink trỏ tới thư mục — CỐ Ý không đuổi theo
    // symlink: dời một thư mục tới qua symlink là đụng vào đích nằm ngoài cwd,
    // đúng thứ ràng buộc "không đụng gì ngoài .claude/ của thư mục con" cấm.
    if (!child.isDirectory()) continue;
    if (child.name.startsWith(".") || child.name === "node_modules") continue;

    const childPath = join(cwd, child.name);
    if (!(await pathExists(join(childPath, WORKSPACE_MARKER_RELATIVE)))) continue;

    let entries: string[] = [];
    try {
      entries = (await fs.readdir(childPath)).sort();
    } catch {
      // Đọc được marker mà không đọc nổi thư mục là ca gần như không thể, nhưng
      // entries rỗng sẽ khiến planner tưởng "chỉ có .claude" và cho dời. Ném lên
      // một entry giả để planner rơi vào nhánh manual (an toàn hơn im lặng).
      entries = ["<không đọc được nội dung thư mục>"];
    }
    found.push({ name: child.name, path: childPath, entries });
  }
  return found;
}
